import { Box } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { ReactElement } from 'react';
import { SalesOrderStatus } from '@synergy/models';
import { WarningAmberOutlined } from '@mui/icons-material';
import { tokens } from '@synergy/theme-palo';

import { activitiesSelector } from '../../../common/selectors/activity-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';

const c = tokens.color;

/**
 * An order over the member's shop limit, released by the member: by whom and
 * when, at the top of the mask. The order keeps only who checked it; when is
 * the moment the status turned, which the activity log has (#2259).
 */
export const PaloSalesOrderApprovalNotice = ({ status }: { status?: string }): ReactElement | null => {
    const activities = useAppSelector(activitiesSelector);

    if (SalesOrderStatus.CompletedWithApproval !== status) {
        return null;
    }

    const approval = [...activities]
        .reverse()
        .find(activity => SalesOrderStatus.CompletedWithApproval === activity.changeSet?.['status']?.newValue);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                mb: '6px',
                padding: '5px 10px',
                fontSize: '12.5px',
                color: c.warnInk,
                backgroundColor: c.warnSoft,
                border: `1px solid ${c.warnLine}`,
                borderRadius: tokens.radius.small + 'px',
            }}
        >
            <WarningAmberOutlined sx={{ fontSize: 16 }} />
            <Box component="b" sx={{ fontWeight: 620 }}>
                <FormattedMessage id="app.salesOrders.detail.limitExceeded" />
            </Box>
            {approval ? (
                <span>
                    {' – '}
                    <FormattedMessage
                        id="app.salesOrders.detail.limitApprovedBy"
                        values={{
                            date: (
                                <FormattedDate value={approval.loggedAt} day="2-digit" month="2-digit" year="numeric" />
                            ),
                            time: <FormattedTime value={approval.loggedAt} />,
                            name: approval.user?.fullName ?? approval.username ?? '–',
                        }}
                    />
                </span>
            ) : (
                <span>
                    {' – '}
                    <FormattedMessage id="app.salesOrders.detail.limitApproved" />
                </span>
            )}
        </Box>
    );
};
